/**
 * Build-time resolver for the Administration link's destination
 * (ROADS-477, Phase 1A of ADR-0013).
 *
 * When the admin UI is co-located with the learner PWA on
 * `staging.roadbridge.app`, the Administration link is a same-origin
 * SPA navigation (`<NavLink to="/admin/users">`). When the admin UI is
 * isolated on a separate subdomain (`admin.staging.roadbridge.app`),
 * the link must do a full page load to the admin origin so the admin
 * SPA boots under its own bundle.
 *
 * The build-time env var `VITE_ADMIN_URL` controls which mode is
 * active. Set to a full origin (e.g. `https://admin.staging.roadbridge.app`)
 * to enable cross-origin navigation; leave unset (or blank) to keep
 * same-origin SPA routing.
 *
 * Returns both the resolved `href` and a `external` flag so the
 * caller can pick between `<a>` (external, full reload) and
 * `<NavLink>` (internal, SPA route) without re-deriving the decision.
 */
export interface ResolvedAdminUrl {
  href: string;
  external: boolean;
}

const SCHEME_RE = /^https?:\/\//;

export function resolveAdminUrl(
  path: string,
  envValue: string | undefined,
): ResolvedAdminUrl {
  if (!envValue || !SCHEME_RE.test(envValue)) {
    return { href: path, external: false };
  }
  const origin = envValue.replace(/\/$/, "");
  return { href: `${origin}${path}`, external: true };
}
